import React, { useState, useEffect } from 'react';
import { CheckCircle, Phone, Calendar, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { storageController } from '../../controllers/StorageController';
import { FormRouteEnum } from '../../routes/FormRoutes';
import { useFormRouting } from '../../routes/FormRoutes';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ScheduleCallForm from './ScheduleCallForm';
import { isBusinessHours, isHoliday, getHolidayName } from '../../utils/businessHours';
import { getBrowserTimezone } from '../../utils/timezones';
import ErrorMessage from '../ui/ErrorMessage';
import ApplicationProgress from '../ApplicationProgress';
import PhoneDialer from '../PhoneDialer';
import { useHubspot } from '../../hooks/useHubspot';
import { settings } from '../../config/settings';
import { formatCurrency } from '../../utils/formatting';
import { TCPACompliance } from '../../utils/tcpaCompliance';

type SuccessStepProps = {
  contactCenterAvailability: 'none' | 'low' | 'high' | null;
  onScheduleAppointment?: (time: Date) => Promise<boolean>;
  onInitiateCall: () => Promise<{ success: boolean; from?: string; error?: string }>;
  isBpoAgent?: boolean;
};

export default function SuccessStep({ 
  contactCenterAvailability, 
  onScheduleAppointment,
  onInitiateCall,
  isBpoAgent = false
}: SuccessStepProps) {
  const { navigateToRoute } = useFormRouting();
  const applicationData = storageController.getApplicationData();
  const customerPhoneNumber = applicationData?.phone || '';
  const firstName = storageController.getContactFirstName() || '';
  const debtAmount = storageController.getDebtAmount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(storageController.getScheduledTime());
  const [isCallInitiated, setIsCallInitiated] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showScheduleButton, setShowScheduleButton] = useState(false); // New state for showing schedule button
  const hubspot = useHubspot();

  // Check if within TCPA calling hours
  const isWithinCallHours = TCPACompliance.isWithinCallHours();
  const today = new Date();
  const isHolidayToday = isHoliday(today);
  const holidayName = getHolidayName(today);

  // Redirect if not successful
  if (!storageController.isApplicationSuccessful()) {
    navigateToRoute(FormRouteEnum.START);
    return null;
  }

  const formatAppointmentTime = (date: Date): string => {
    const timezone = getBrowserTimezone();
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, "EEEE, MMMM d 'at' h:mm a");
  };

  const handleScheduleCall = async (selectedTime: Date) => {
    setLoading(true);
    setError(null);

    try {
      if (onScheduleAppointment) {
        const success = await onScheduleAppointment(selectedTime);
        
        if (success) {
          storageController.setScheduledTime(selectedTime);
          setScheduledTime(selectedTime);
          setShowScheduleForm(false);

          hubspot.trackEvent('appointment_scheduled', {
            email: applicationData?.email,
            form_step: 'success',
            scheduled_time: selectedTime.toISOString(),
            timezone: getBrowserTimezone()
          });
        } else {
          throw new Error('Failed to schedule appointment');
        }
      }
    } catch (error) {
      console.error('Failed to schedule call:', error);
      setError('Unable to schedule your call. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleCallInitiated = () => {
    setIsCallInitiated(true);
  };

  // New handler for countdown complete event
  const handleCountdownComplete = () => {
    setShowScheduleButton(true);
  };

  // Show loading state while checking availability
  if (contactCenterAvailability === null) {
    return (
      <div id="success-step-loading\" className="text-center space-y-6">
        <div className="w-16 h-16 bg-[#b3905e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="relative">
            <CheckCircle className="w-8 h-8 text-[#b3905e]" />
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5">
              <Loader2 className="w-4 h-4 text-[#b3905e] animate-spin" />
            </div>
          </div>
        </div>
        
        <Title 
          id="success-step-loading-title"
          name="success-step-loading-title"
          as="h1" 
          className="mb-2"
        >
          Application Received!
        </Title>
        
        <div className="max-w-sm mx-auto">
          <Description 
            id="success-step-loading-description"
            name="success-step-loading-description"
            size="lg"
          >
            Thank you <span className="font-medium">{firstName}</span>, your application has been received. We're checking our team's availability to assist you...
          </Description>
        </div>

        <div className="w-full md:w-1/2 mx-auto">
          <div className="bg-[#b3905e]/5 rounded-lg p-6">
            <div className="flex items-center justify-center gap-3 text-[#b3905e] animate-pulse">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-medium">Checking availability</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show scheduler if no availability or scheduler is requested
  if ((contactCenterAvailability === 'none' || showScheduleForm) && !scheduledTime) {
    return (
      <>
        <div id="success-step-schedule" className="text-center mb-6">
          <Title 
            id="success-step-schedule-title"
            name="success-step-schedule-title"
            as="h2" 
            className="mb-2"
          >
            Congratulations, {firstName}!
          </Title>
          <div className="text-2xl font-bold text-[#b3905e] mb-4">
            You're eligible for up to {formatCurrency(debtAmount)}
          </div>
        </div>
        <ScheduleCallForm 
          firstName={firstName}
          phoneNumber={customerPhoneNumber}
          onSchedule={handleScheduleCall} 
        />
        {error && (
          <ErrorMessage 
            message={error}
            className="mt-4"
          />
        )}
        {showScheduleForm && (
          <Button
            id="success-step-back"
            name="success-step-back"
            onClick={() => setShowScheduleForm(false)}
            variant="outline"
            size="default"
            fullWidth
          >
            Back to Contact Options
          </Button>
        )}
      </>
    );
  }

  return (
    <div id="success-step" className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-[#b3905e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-[#b3905e]" />
        </div>
        
        <Title 
          id="success-step-title"
          name="success-step-title"
          as="h2" 
          className="mb-2"
        >
          {scheduledTime ? 'Application Received!' : `Congratulations, ${firstName}!`}
        </Title>
        <Description 
          id="success-step-description"
          name="success-step-description"
          size="lg"
        >
          {scheduledTime ? (
            <span>
              Thank you <span className="font-medium">{firstName}</span>, we look forward to speaking with you on{' '}
              <span className="font-medium">{formatAppointmentTime(scheduledTime)}</span>
            </span>
          ) : (
            <div className="text-2xl font-bold text-[#b3905e]">
              You're eligible for up to {formatCurrency(debtAmount)}
            </div>
          )}
        </Description>
      </div>
      
      <div className="w-full md:w-2/3 mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
          {scheduledTime ? (
            <>
              <div className="flex items-center justify-center gap-2 text-[#b3905e]">
                <Calendar className="w-5 h-5" />
                <span className="font-medium">Appointment Scheduled</span>
              </div>
              <Description 
                id="success-step-immediate-assistance"
                name="success-step-immediate-assistance"
                className="text-center"
              >
                <span>For immediate assistance before your appointment, call:</span>
              </Description>
              <Button
                id="success-step-call-support"
                name="success-step-call-support"
                variant="secondary"
                size="lg"
                icon={<Phone className="w-5 h-5" />}
                phoneNumber={settings.phone.supportRaw}
                fullWidth
              >
                {settings.phone.support}
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              {/* High availability - Show auto dialer (unless BPO agent or holiday) */}
              {(contactCenterAvailability === 'high' || contactCenterAvailability === 'low') && !isCallInitiated && !isBpoAgent && isWithinCallHours && !isHolidayToday && (
                <>
                  <PhoneDialer 
                    phoneNumber={customerPhoneNumber} 
                    fallbackNumber={settings.phone.support}
                    autoTrigger={true}
                    onCallInitiated={handleCallInitiated}
                    onCountdownComplete={handleCountdownComplete} // Pass the event handler
                    onInitiateCall={onInitiateCall}
                  />
                  {/* Show schedule button after countdown completes */}
                  {showScheduleButton && (
                    <Button
                      id="success-step-schedule-appointment"
                      name="success-step-schedule-appointment"
                      onClick={() => setShowScheduleForm(true)}
                      variant="outline"
                      size="default"
                      icon={<Calendar className="w-5 h-5" />}
                      fullWidth
                    >
                      Schedule Appointment
                    </Button>
                  )}
                </>
              )}

              {/* Outside TCPA hours - Show schedule button only */}
              {!isWithinCallHours && !isCallInitiated && (
                <>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Schedule a Callback
                    </h3>
                    <p className="text-gray-600 mb-4">
                      We have some agents awaiting your call. Please call us directly or schedule a callback.
                    </p>
                  </div>
                  <Button
                    id="success-step-call-support"
                    name="success-step-call-support"
                    variant="secondary"
                    size="lg"
                    icon={<Phone className="w-5 h-5" />}
                    phoneNumber={settings.phone.supportRaw}
                    fullWidth
                  >
                    {settings.phone.support}
                  </Button>
                  <Button
                    id="success-step-schedule-only"
                    name="success-step-schedule-only"
                    onClick={() => setShowScheduleForm(true)}
                    variant="primary"
                    size="lg"
                    icon={<Calendar className="w-5 h-5" />}
                    fullWidth
                  >
                    Schedule Appointment
                  </Button>
                </>
              )}

              {/* Show call initiated message */}
              {isCallInitiated && (
                <div className="text-center space-y-2 bg-[#b3905e]/5 p-4 rounded-lg animate-fade-in">
                  <p className="text-sm text-gray-600">
                    Incoming call from:
                  </p>
                  <p className="text-lg font-semibold text-[#b3905e]">
                    {settings.phone.support}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}